import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectUIStates,
  selectUser,
  setUIStates,
} from "../../../redux/features/appData";
import {
  apiCreateVibeProcess,
  apiGetVibeProcessesForWorkspace,
  apiUpdateVibeProcess,
  apiGetWorkflowsForWorkspace,
} from "../../../services/WorkflowService";
import { apiGetEmployeeRecordsForWorkspace } from "../../../services/EmployeeRecordService";
import {
  EmployeeRecord,
  EmployeeReferenceType,
  VibeProcess,
  Workflow,
} from "winslow-core";
import { Button, Icon } from "../../../ds2/components";
import {
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  Settings,
  Code,
  User,
  Building,
} from "lucide-react";
import "./AppVibeProcesses.css";
import "../../../ds2/components/Table.css";
import { ModalVibeCodeBuilder } from "./ModalVibeCodeBuilder";
import { ActionLink } from "../../../ds2/components/ActionLink";
import { erGetCPValue } from "../app-employee-records/employeeRecordUtils";
import { openEmployeeProfileSidebar } from "../../../redux/features/agent/agentSlice";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import toast from "react-hot-toast";
import { toastSuccess, toastFail } from "../../../config";

export const AppVibeProcesses = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storeUser = useSelector(selectUser);
  const uiStates = useSelector(selectUIStates);

  const [vibeProcesses, setVibeProcesses] = useState<VibeProcess[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [employeeRecords, setEmployeeRecords] = useState<EmployeeRecord[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [activeFilter, setActiveFilter] = useState<"Active" | "Inactive" | "All">("Active");

  const [showVibeCodeBuilder, setShowVibeCodeBuilder] =
    useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  useEffect(() => {
    dispatch(setUIStates({ ...uiStates, hrisLocation: "Vibes" }));
  }, []);

  useEffect(() => {
    if (storeUser?.currentWorkspaceId) {
      (async () => {
        const [vibeProcesses, employeeRecords, workflows] = await Promise.all([
          apiGetVibeProcessesForWorkspace(storeUser?.currentWorkspaceId || ""),
          apiGetEmployeeRecordsForWorkspace(
            storeUser?.currentWorkspaceId || ""
          ),
          apiGetWorkflowsForWorkspace(storeUser?.currentWorkspaceId || ""),
        ]);
        setVibeProcesses(vibeProcesses ? vibeProcesses : []);
        setEmployeeRecords(employeeRecords ? employeeRecords : []);
        setWorkflows(workflows ? workflows : []);
        setLoading(false);
      })();
    }
  }, [storeUser?.currentWorkspaceId]);

  const loadVibeProcesses = async () => {
    if (!storeUser?.currentWorkspaceId) return;
    const [vibeProcessesData, workflowsData] = await Promise.all([
      apiGetVibeProcessesForWorkspace(storeUser?.currentWorkspaceId || ""),
      apiGetWorkflowsForWorkspace(storeUser?.currentWorkspaceId || ""),
    ]);
    setVibeProcesses(vibeProcessesData ? vibeProcessesData : []);
    setWorkflows(workflowsData ? workflowsData : []);
    setLoading(false);
  };

  const handleCreateNew = () => {
    setShowCreateModal(true);
  };

  const handleVibeCodeCreate = async (
    prompt: string,
    workflowPrompt: string
  ) => {
    if (!storeUser) {
      return;
    }

    const payload = {
      creationPrompt: prompt,
      workflowPrompt: workflowPrompt,
      ownerEmployeeRecordId: storeUser?.employeeRecordId || "",
      workspaceId: storeUser?.currentWorkspaceId || "",
    };

    const vibeProcess = await apiCreateVibeProcess(payload);

    if (!vibeProcess) {
      return;
    }

    setShowCreateModal(false);
    navigate(`/app/workflow/edit-vibe/${vibeProcess.id}`);
  };

  const handleEdit = (vibeProcess: VibeProcess) => {
    navigate(`/app/workflow/edit-vibe/${vibeProcess.id}`);
  };

  const handleDelete = async (vibeProcess: VibeProcess) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${vibeProcess.name}"? This action cannot be undone.`
      )
    ) {
      try {
        // TODO: Implement delete API call
        console.log("Delete vibe process:", vibeProcess.id);
        // Reload the list after deletion
        loadVibeProcesses();
      } catch (err) {
        console.error("Error deleting vibe process:", err);
      }
    }
  };

  const handleRun = (vibeProcess: VibeProcess) => {
    // TODO: Implement run functionality
    console.log("Run vibe process:", vibeProcess.id);
  };

  const handleToggleActive = async (vibeProcess: VibeProcess) => {
    try {
      const updatedProcess = await apiUpdateVibeProcess(vibeProcess.id, {
        active: !vibeProcess.active,
      });
      
      if (updatedProcess) {
        toast(
          `Vibe process ${updatedProcess.active ? "activated" : "paused"} successfully`,
          toastSuccess
        );
        // Update the local state
        setVibeProcesses((prev) =>
          prev.map((p) => (p.id === vibeProcess.id ? updatedProcess : p))
        );
      } else {
        toast("Failed to update vibe process status", toastFail);
      }
    } catch (err) {
      console.error("Error toggling vibe process:", err);
      toast("Failed to update vibe process status", toastFail);
    }
  };

  const formatDate = (dateInput: string | Date | null | undefined) => {
    if (!dateInput) return "Unknown";
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    return isNaN(date.getTime()) ? "Invalid Date" : date.toLocaleDateString();
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const getEmployeeAvatar = (employeeRecordId: string) => {
    const employeeRecord = employeeRecords.find(
      (er) => er.id === employeeRecordId
    );
    return employeeRecord?.avatarURL || null;
  };

  const getEmployeeName = (employeeRecordId: string) => {
    const employeeRecord = employeeRecords.find(
      (er) => er.id === employeeRecordId
    );
    if (!employeeRecord) return "";
    return (
      erGetCPValue(employeeRecord, "system-firstName") +
      " " +
      erGetCPValue(employeeRecord, "system-lastName")
    );
  };

  const getActiveFilterOptions = (): GenericSelectOption[] => {
    return [
      { displayName: "Active", value: "Active" },
      { displayName: "Inactive", value: "Inactive" },
      { displayName: "All", value: "All" },
    ];
  };

  const filteredVibeProcesses = vibeProcesses.filter((process) => {
    if (activeFilter === "Active") {
      return process.active === true;
    } else if (activeFilter === "Inactive") {
      return process.active === false;
    } else if (activeFilter === "All") {
      return true; // Show all vibe processes
    }
    return true; // Default to showing all
  });

  console.log("[AppVibeProcesses] Active filter:", activeFilter, "Total processes:", vibeProcesses.length, "Filtered:", filteredVibeProcesses.length);

  const getWorkflowCount = (vibeProcessId: string): number => {
    return workflows.filter((workflow) => workflow.vibeProcessId === vibeProcessId).length;
  };

  if (loading) {
    return (
      <div className="app-vibe-processes">
        <div className="app-vibe-processes-loading">
          <div className="app-vibe-processes-spinner"></div>
          <p>Loading vibe processes...</p>
        </div>
      </div>
    );
  }

  console.log("[AppVibeProcesses] vibeProcesses:", vibeProcesses);

  return (
    <div className="app-vibe-processes">
      {/* Header */}
      <div className="app-vibe-processes-header">
        <div className="ds2-table-header">
          Vibe Processes
          {/* <span>{vibeProcesses.length} processes in this workspace</span> */}
          <div className="ds2-table-header-actions">
            <div className="app-vibe-processes-filter">
              <label>Status:</label>
              <GenericSelect
                selected={getActiveFilterOptions().find(
                  (option) => option.value === activeFilter
                )}
                options={getActiveFilterOptions()}
                onChange={(value) => {
                  const filterValue = value as "Active" | "Inactive" | "All";
                  console.log("[AppVibeProcesses] Setting active filter to:", filterValue);
                  setActiveFilter(filterValue);
                }}
              />
            </div>
            <ActionLink
              variant="primary"
              size="md"
              leftIcon={<Plus size={16} />}
              label="New Vibe"
              onClick={handleCreateNew}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="app-vibe-processes-content">
        {error && (
          <div className="app-vibe-processes-error">
            <p>{error}</p>
            <Button
              variant="primary"
              size="sm"
              label="Retry"
              onClick={loadVibeProcesses}
            />
          </div>
        )}

        {!loading && !error && vibeProcesses.length === 0 && (
          <div className="app-vibe-processes-empty">
            <div className="app-vibe-processes-empty-content">
              <Code size={48} />
              <h3>No Vibe Processes</h3>
              <p>
                Create your first vibe process to get started with automated
                workflows.
              </p>
              <Button
                variant="primary"
                leftIcon={<Plus size={16} />}
                label="Create"
                onClick={handleCreateNew}
              />
            </div>
          </div>
        )}

        {!loading && !error && (vibeProcesses.length > 0 || filteredVibeProcesses.length > 0) && (
          <div className="ds2-table-container">
            <table className="ds2-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Conversational Trigger</th>
                  <th>Status</th>
                  <th>Owner</th>
                  <th>Created</th>
                  <th>Uses</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVibeProcesses.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="app-vibe-processes-empty-row">
                      No {activeFilter.toLowerCase()} vibe processes found
                    </td>
                  </tr>
                ) : (
                  filteredVibeProcesses.map((process: VibeProcess) => (
                  <tr key={process.id}>
                    <td>{process.name}</td>
                    <td>
                      {process.description
                        ? truncateText(process.description, 60)
                        : "-"}
                    </td>
                    <td>
                      <div
                        className={`app-vibe-processes-status ${process.active ? "active" : "inactive"}`}>
                        <div className="app-vibe-processes-status-dot"></div>
                        <span>{process.active ? "Active" : "Inactive"}</span>
                      </div>
                    </td>
                    <td>
                      <div
                        className="app-vibe-processes-owner"
                        onClick={() => {
                          if (process.ownerEmployeeRecordId) {
                            dispatch(
                              openEmployeeProfileSidebar({
                                employeeId: process.ownerEmployeeRecordId,
                                type: EmployeeReferenceType.EMPLOYEE_RECORD,
                              })
                            );
                          }
                        }}>
                        {getEmployeeAvatar(process.ownerEmployeeRecordId) ? (
                          <img
                            src={
                              getEmployeeAvatar(process.ownerEmployeeRecordId)!
                            }
                            alt={getEmployeeName(process.ownerEmployeeRecordId)}
                          />
                        ) : (
                          <User size={14} />
                        )}
                        <span>
                          {getEmployeeName(process.ownerEmployeeRecordId)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="app-vibe-processes-created">
                        <span>{formatDate(process.createdAt)}</span>
                      </div>
                    </td>
                    <td className="app-vibe-processes-uses-cell">
                      <div className="app-vibe-processes-uses">
                        <span>{getWorkflowCount(process.id)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="app-vibe-processes-actions-group">
                        <Icon
                          variant="tertiary"
                          size="md"
                          icon={<Edit size={16} />}
                          onClick={() => handleEdit(process)}
                          title="Edit"
                        />
                        <Icon
                          variant="tertiary"
                          size="md"
                          icon={
                            process.active ? (
                              <Pause size={16} />
                            ) : (
                              <Play size={16} />
                            )
                          }
                          onClick={() => handleToggleActive(process)}
                          title={process.active ? "Pause" : "Activate"}
                        />
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Vibe Process Modal */}
      <ModalVibeCodeBuilder
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleVibeCodeCreate}
        onCancel={() => setShowCreateModal(false)}
      />
    </div>
  );
};
